/*
global
    BSTicker,
*/

/**
 * @class
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @param {(0|90|180|270)} rotation
 */
function BSTickerWrapper(x, y, width, height, rotation) {
    rotation = rotation || 0;

    Object.defineProperties(this, {
        /**
         * @member {number} BSTickerWrapper#_x
         * @protected
         */
        _x: {
            value: x,
            writable: true,
        },

        /**
         * @member {number} BSTickerWrapper#_y
         * @protected
         */
        _y: {
            value: y,
            writable: true,
        },

        /**
         * @member {number} BSTickerWrapper#_width
         * @protected
         */
        _width: {
            value: width,
            writable: true,
        },

        /**
         * @member {number} BSTickerWrapper#_height
         * @protected
         */
        _height: {
            value: height,
            writable: true,
        },

        /**
         * @member {Array.<string>} BSTickerWrapper#_strings
         * @protected
         */
        _strings: {
            value: [],
            writable: true,
        },

        /**
         * @member {number} BSTickerWrapper#_backgroundColor
         * @protected
         */
        _backgroundColor: {
            value: 0xff000000,
            writable: true,
        },

        /**
         * @member {number} BSTickerWrapper#_foregroundColor
         * @protected
         */
        _foregroundColor: {
            value: 0xffffffff,
            writable: true,
        },

        /**
         * @member {number|string} BSTickerWrapper#_separator
         * @protected
         */
        _separator: {
            value: BSTickerWrapper.SEPARATOR_DIAMOND,
            writable: true,
        },

        /**
         * @member {number} BSTickerWrapper#_pixelsPerSecond
         * @protected
         */
        _pixelsPerSecond: {
            value: 100,
            writable: true,
        },

        /**
         * @member {number} BSTickerWrapper#x
         */
        x: {
            get: function () {
                return this._x;
            },
            set: function (x) {
                this.setPosition(x, this._y);
            },
            enumerable: true,
        },

        /**
         * @member {number} BSTickerWrapper#y
         */
        y: {
            get: function () {
                return this._y;
            },
            set: function (y) {
                this.setPosition(this._x, y);
            },
            enumerable: true,
        },

        /**
         * @member {number} BSTickerWrapper#width
         */
        width: {
            get: function () {
                return this._width;
            },
            set: function (width) {
                this.setSize(width, this._height);
            },
            enumerable: true,
        },

        /**
         * @member {number} BSTickerWrapper#height
         */
        height: {
            get: function () {
                return this._height;
            },
            set: function (height) {
                this.setSize(this._width, height);
            },
            enumerable: true,
        },

        /**
         * @member {(0|90|180|270)} BSTickerWrapper#rotation
         * @readonly
         */
        rotation: {
            value: rotation,
            enumerable: true,
            writable: false,
        },
    });

    // Start Ticker
    this.start();
}

/**
 * Ticker already exists error message
 * @type {string}
 * @constant
 */
BSTickerWrapper.ERROR_TICKER_EXISTS = 'BrightSign ticker already exists.';

/**
 * Ticker does not exist error message
 * @type {string}
 * @constant
 */
BSTickerWrapper.ERROR_NO_TICKER_EXISTS = 'BrightSign ticker does not exist.';

/**
 * Circle Separator Code
 * @type {number}
 * @constant
 */
BSTickerWrapper.SEPARATOR_CIRCLE = 0;

/**
 * Diamond Separator Code
 * @type {number}
 * @constant
 */
BSTickerWrapper.SEPARATOR_DIAMOND = 1;

/**
 * Square Separator Code
 * @type {number}
 * @constant
 */
BSTickerWrapper.SEPARATOR_SQUARE = 2;

/**
 * @type {BSTicker}
 */
BSTickerWrapper.prototype.bsTicker = undefined;

/**
 * Combine separate color/alpha intensities into a single hex number
 * @param {number} alpha - Alpha component (Range: 0-255)
 * @param {number} red - Red color component (Range: 0-255)
 * @param {number} green - Green color component (Range: 0-255)
 * @param {number} blue - Blue color component (Range: 0-255)
 * @returns {number} argb hex number
 */
BSTickerWrapper.argb = function (alpha, red, green, blue) {
    var hexAlpha = alpha * 0x01000000;
    var hexRed = red * 0x00010000;
    var hexGreen = green * 0x00000100;
    var hexBlue = blue * 0x00000001;

    return hexAlpha + hexRed + hexGreen + hexBlue;
};

/**
 * @throws Will throw an error if the ticker is already running.
 */
BSTickerWrapper.prototype.start = function () {
    if (this.bsTicker) {
        throw new Error(BSTickerWrapper.ERROR_TICKER_EXISTS);
    }

    this.bsTicker = new BSTicker(this.x, this.y, this.width, this.height, this.rotation);

    // todo create getter/setter
    this.bsTicker.SetBackgroundColor(this._backgroundColor);
    this.bsTicker.SetForegroundColor(this._foregroundColor);

    this.setSeparator(this._separator);

    // todo create getter/setter
    this.bsTicker.SetPixelsPerSecond(this._pixelsPerSecond);

    this._strings.forEach(
        function (string) {
            this.bsTicker.AddString(string);
        }.bind(this)
    );
};

/**
 * @throws Will throw an error if the ticker is *not* already running.
 */
BSTickerWrapper.prototype.stop = function () {
    if (!this.bsTicker) {
        throw new Error(BSTickerWrapper.ERROR_NO_TICKER_EXISTS);
    }

    this.bsTicker.Shutdown();
    delete this.bsTicker;
};

/**
 * @throws Will throw an error if the ticker is *not* already running.
 */
BSTickerWrapper.prototype.restart = function () {
    this.stop();
    this.start();
};

/**
 * @see {@link http://docs.brightsign.biz/display/DOC/BSTicker#BSTicker-Methods}
 * @param {string} string
 * @returns {boolean}
 * @throws Will throw an error if the ticker is *not* already running.
 */
BSTickerWrapper.prototype.AddString = function (string) {
    if (!this.bsTicker) {
        throw new Error(BSTickerWrapper.ERROR_NO_TICKER_EXISTS);
    }

    if (this.bsTicker.AddString(string) !== false) {
        this._strings.push(string);
        return true;
    }

    return false;
};

/**
 * @see {@link http://docs.brightsign.biz/display/DOC/BSTicker#BSTicker-Methods}
 * @param {number} count
 * @returns {boolean}
 * @throws Will throw an error if the ticker is *not* already running.
 */
BSTickerWrapper.prototype.PopStrings = function (count) {
    if (!this.bsTicker) {
        throw new Error(BSTickerWrapper.ERROR_NO_TICKER_EXISTS);
    }

    if (this.bsTicker.PopStrings(count)) {
        this._strings.splice(0, count);
        return true;
    }

    return false;
};

/**
 * Changes the position and size of the ticker (size changes will reset the
 * ticker)
 * @see {@link http://docs.brightsign.biz/display/DOC/BSTicker#BSTicker-Methods}
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @returns {boolean}
 * @throws Will throw an error if the ticker is *not* already running.
 */
BSTickerWrapper.prototype.SetRectangle = function (x, y, width, height) {
    if (!this.bsTicker) {
        throw new Error(BSTickerWrapper.ERROR_NO_TICKER_EXISTS);
    }

    var sizeChanged = width != this.width || height != this.height;

    if (sizeChanged) {
        this.stop();

        this._x = x;
        this._y = y;
        this._width = width;
        this._height = height;

        this.start();

        return true;
    } else if (this.bsTicker.SetRectangle(x, y, width, height)) {
        this._x = x;
        this._y = y;
        this._width = width;
        this._height = height;

        return true;
    }

    return false;
};

/**
 * Changes the position of the ticker
 * @param {number} x
 * @param {number} y
 * @returns {boolean}
 * @throws Will throw an error if the ticker is *not* already running.
 */
BSTickerWrapper.prototype.setPosition = function (x, y) {
    return this.SetRectangle(x, y, this.width, this.height);
};

/**
 * Changes the size of the ticker (size changes will reset the ticker)
 * @param {number} width
 * @param {number} height
 * @returns {boolean}
 * @throws Will throw an error if the ticker is *not* already running.
 */
BSTickerWrapper.prototype.setSize = function (width, height) {
    return this.SetRectangle(this.x, this.y, width, height);
};

/**
 * @method SetBackgroundColor
 * @instance
 * @see {@link http://docs.brightsign.biz/display/DOC/BSTicker#BSTicker-Methods}
 * @param {number} argb - Hex number representing Alpha, Red, Green, Blue
 * @returns {boolean}
 * @throws Will throw an error if the ticker is *not* already running.
 * @memberof BSTickerWrapper
 */
/**
 * @method SetBackgroundColor^2
 * @instance
 * @see {@link http://docs.brightsign.biz/display/DOC/BSTicker#BSTicker-Methods}
 * @param {number} alpha - Alpha component (Range: 0-255)
 * @param {number} red - Red color component (Range: 0-255)
 * @param {number} green - Green color component (Range: 0-255)
 * @param {number} blue - Blue color component (Range: 0-255)
 * @returns {boolean}
 * @throws Will throw an error if the ticker is *not* already running.
 * @memberof BSTickerWrapper
 */
BSTickerWrapper.prototype.SetBackgroundColor = function (alpha, red, green, blue) {
    if (!this.bsTicker) {
        throw new Error(BSTickerWrapper.ERROR_NO_TICKER_EXISTS);
    }

    var hexColor;

    if (arguments.length === 1) {
        hexColor = arguments[0];
    } else {
        hexColor = BSTickerWrapper.argb(alpha, red, green, blue);
    }

    if (this.bsTicker.SetBackgroundColor(hexColor)) {
        this._backgroundColor = hexColor;
        return true;
    }

    return false;
};

/**
 * @method SetForegroundColor
 * @instance
 * @see {@link http://docs.brightsign.biz/display/DOC/BSTicker#BSTicker-Methods}
 * @param {number} argb - Hex number representing Alpha, Red, Green, Blue
 * @returns {boolean}
 * @throws Will throw an error if the ticker is *not* already running.
 * @memberof BSTickerWrapper
 */
/**
 * @method SetForegroundColor^2
 * @instance
 * @see {@link http://docs.brightsign.biz/display/DOC/BSTicker#BSTicker-Methods}
 * @param {number} alpha - Alpha component (Range: 0-255)
 * @param {number} red - Red color component (Range: 0-255)
 * @param {number} green - Green color component (Range: 0-255)
 * @param {number} blue - Blue color component (Range: 0-255)
 * @returns {boolean}
 * @throws Will throw an error if the ticker is *not* already running.
 * @memberof BSTickerWrapper
 */
BSTickerWrapper.prototype.SetForegroundColor = function (alpha, red, green, blue) {
    if (!this.bsTicker) {
        throw new Error(BSTickerWrapper.ERROR_NO_TICKER_EXISTS);
    }

    var hexColor;

    if (arguments.length === 1) {
        hexColor = arguments[0];
    } else {
        hexColor = BSTickerWrapper.argb(alpha, red, green, blue);
    }

    if (this.bsTicker.SetForegroundColor(hexColor)) {
        this._foregroundColor = hexColor;
        return true;
    }

    return false;
};

/**
 * @param {number|string} separator
 * @returns {boolean}
 * @throws Will throw an error if the ticker is *not* already running.
 */
BSTickerWrapper.prototype.setSeparator = function (separator) {
    if (!this.bsTicker) {
        throw new Error(BSTickerWrapper.ERROR_NO_TICKER_EXISTS);
    }

    var successful;

    if (typeof separator === 'string') {
        successful = this.bsTicker.SetSeparatorString(separator);
    } else if (separator === BSTickerWrapper.SEPARATOR_CIRCLE) {
        successful = this.bsTicker.SetSeparatorCircle();
    } else if (separator === BSTickerWrapper.SEPARATOR_DIAMOND) {
        successful = this.bsTicker.SetSeparatorDiamond();
    } else if (separator === BSTickerWrapper.SEPARATOR_SQUARE) {
        successful = this.bsTicker.SetSeparatorSquare();
    }

    if (successful) {
        this._separator = separator;
        return true;
    }

    return false;
};

/**
 * @see {@link http://docs.brightsign.biz/display/DOC/BSTicker#BSTicker-Methods}
 * @param {string} separator
 * @returns {boolean}
 * @throws Will throw an error if the ticker is *not* already running.
 */
BSTickerWrapper.prototype.SetSeparatorString = function (separator) {
    return this.setSeparator(separator);
};

/**
 * @see {@link http://docs.brightsign.biz/display/DOC/BSTicker#BSTicker-Methods}
 * @returns {boolean}
 * @throws Will throw an error if the ticker is *not* already running.
 */
BSTickerWrapper.prototype.SetSeparatorCircle = function () {
    return this.setSeparator(BSTickerWrapper.SEPARATOR_CIRCLE);
};

/**
 * @see {@link http://docs.brightsign.biz/display/DOC/BSTicker#BSTicker-Methods}
 * @returns {boolean}
 * @throws Will throw an error if the ticker is *not* already running.
 */
BSTickerWrapper.prototype.SetSeparatorDiamond = function () {
    return this.setSeparator(BSTickerWrapper.SEPARATOR_DIAMOND);
};

/**
 * @see {@link http://docs.brightsign.biz/display/DOC/BSTicker#BSTicker-Methods}
 * @returns {boolean}
 * @throws Will throw an error if the ticker is *not* already running.
 */
BSTickerWrapper.prototype.SetSeparatorSquare = function () {
    return this.setSeparator(BSTickerWrapper.SEPARATOR_SQUARE);
};

/**
 * @see {@link http://docs.brightsign.biz/display/DOC/BSTicker#BSTicker-Methods}
 * @returns {number}
 * @throws Will throw an error if the ticker is *not* already running.
 */
BSTickerWrapper.prototype.SetPixelsPerSecond = function (pixelsPerSecond) {
    if (!this.bsTicker) {
        throw new Error(BSTickerWrapper.ERROR_NO_TICKER_EXISTS);
    }

    this._pixelsPerSecond = pixelsPerSecond;
    return this.bsTicker.SetPixelsPerSecond(pixelsPerSecond);
};

export { BSTickerWrapper };
